import { NavigationGuide, NavigationSection } from '../fetch-data/graphql';
import { normalizeSectionId } from './query-param-helpers';

export const isValidNavigation = (
  navigation: NavigationGuide | undefined
): boolean => {
  return Boolean(navigation);
};

export const findSectionRecursive = (
  sections: NavigationSection[],
  targetId: string
): NavigationSection | undefined => {
  const normalizedTargetId = normalizeSectionId(targetId);

  for (const section of sections) {
    const normalizedSectionId = normalizeSectionId(section.sectionId);
    if (normalizedSectionId === normalizedTargetId) return section;
    if (section.subsections?.length) {
      const found = findSectionRecursive(section.subsections, targetId);
      if (found) return found;
    }
  }
  return undefined;
};

export const isSectionIndexable = (section: NavigationSection): boolean => {
  const hasSubsections = section.subsections && section.subsections.length > 0;

  return !hasSubsections;
};

export const findFirstIndexableSection = (
  sections: NavigationSection[]
): NavigationSection | null => {
  for (const section of sections) {
    const hasSubsections =
      section.subsections && section.subsections.length > 0;

    const isIndexable = isSectionIndexable(section);

    if (isIndexable) return section;

    if (hasSubsections) {
      const child = findFirstIndexableSection(section.subsections ?? []);
      if (child) return child;
    }
  }
  return null;
};

export const sectionContains = (section: any, targetId: string): boolean => {
  const normalizedTargetId = normalizeSectionId(targetId);
  const normalizedSectionId = normalizeSectionId(section.sectionId);
  if (normalizedSectionId === normalizedTargetId) return true;
  return section.subsections?.some((sub: any) =>
    sectionContains(sub, targetId)
  );
};

/**
 * Flattens all indexable sections from the navigation tree into a flat array
 */
export const getAllIndexableSections = (
  sections: NavigationSection[]
): NavigationSection[] => {
  const result: NavigationSection[] = [];

  const traverse = (sections: NavigationSection[]) => {
    for (const section of sections) {
      if (isSectionIndexable(section)) {
        result.push(section);
      }
      if (section.subsections?.length) {
        traverse(section.subsections);
      }
    }
  };

  traverse(sections);
  return result;
};

/**
 * Gets the previous and next indexable sections relative to the current section
 */
export const getPreviousAndNextSections = (
  sections: NavigationSection[],
  currentSectionId: string
): { previous: NavigationSection | null; next: NavigationSection | null } => {
  const allIndexable = getAllIndexableSections(sections);
  const normalizedTargetId = normalizeSectionId(currentSectionId);
  const currentIndex = allIndexable.findIndex(
    (s) => normalizeSectionId(s.sectionId) === normalizedTargetId
  );

  if (currentIndex === -1) {
    return { previous: null, next: null };
  }

  return {
    previous: currentIndex > 0 ? allIndexable[currentIndex - 1] : null,
    next:
      currentIndex < allIndexable.length - 1
        ? allIndexable[currentIndex + 1]
        : null,
  };
};

/**
 * Recursively normalize all sectionIds in a navigation section
 */
export const normalizeNavigationSection = (
  section: NavigationSection
): NavigationSection => {
  const normalizedSectionId = normalizeSectionId(section.sectionId);
  const normalizedSubsections = section.subsections
    ? section.subsections.map(normalizeNavigationSection)
    : undefined;
  const originalSectionId = section.sectionId;

  return {
    ...section,
    sectionId: normalizedSectionId,
    originalSectionId,
    subsections: normalizedSubsections,
  };
};

/**
 * Normalize all sectionIds in a NavigationGuide
 */
export const normalizeNavigation = (
  navigation: NavigationGuide | null
): NavigationGuide | null => {
  if (!navigation) return null;

  const normalizedSections = navigation.sections
    ? navigation.sections.map(normalizeNavigationSection)
    : undefined;

  return {
    ...navigation,
    sections: normalizedSections,
  };
};
